// Ads for free players (contracts/ads.md §2, §5): the ads.* settings as backend/api validates them, their
// defaults (Google's test ids), labels for the Monetisation → Ads page, and the unit-id checks. Shared by
// the page and the mock (src/mocks/db.ts validates PUT /config/ads.* with these schemas).

import { z } from "zod";

// ---------------------------------------------------------------- ids

/** AdMob app and unit ids (the panel accepts either separator; the API wants `~` for app ids, `/` for units). */
export const ADMOB_ID = /^ca-app-pub-\d{16}[/~]\d{10}$/;
export const ADMOB_APP_ID = /^ca-app-pub-\d{16}~\d{10}$/;
export const ADMOB_UNIT_ID = /^ca-app-pub-\d{16}\/\d{10}$/;
/** Ad Manager (AdX) ad unit paths: `/<network code>/<unit>[/<unit>…]`. */
export const ADMANAGER_UNIT_PATH = /^\/\d+(\/[\w.-]+)+$/;

export const AD_UNIT_KINDS = ["banner", "interstitial", "rewarded", "rewardedInterstitial", "appOpen"] as const;
export type AdUnitKind = (typeof AD_UNIT_KINDS)[number];

export const UNIT_LABEL: Record<AdUnitKind, string> = {
  banner: "Banner",
  interstitial: "Interstitial",
  rewarded: "Rewarded",
  rewardedInterstitial: "Rewarded interstitial",
  appOpen: "App open",
};

export type IdProvider = "admanager" | "admob";

/** Why a unit id is refused for this provider, or null when it passes. */
export function unitIdProblem(provider: IdProvider, value: string): string | null {
  const v = value.trim();
  if (!v) return "Required.";
  if (provider === "admanager") {
    if (ADMOB_ID.test(v)) return "That's an AdMob id. Ad Manager units are paths: /<network code>/<unit>.";
    return ADMANAGER_UNIT_PATH.test(v) ? null : "Expected an ad unit path: /<network code>/<unit>, e.g. /21775744923/example/rewarded.";
  }
  if (!ADMOB_ID.test(v)) return "Expected ca-app-pub-<16 digits>/<10 digits>.";
  return ADMOB_UNIT_ID.test(v) ? null : "That's an app id (~). Unit ids use a slash: ca-app-pub-…/….";
}

/** Why an app id is refused, or null. Ad Manager may leave it empty (GADIsAdManagerApp). */
export function appIdProblem(provider: IdProvider, value: string): string | null {
  const v = value.trim();
  if (!v) return provider === "admanager" ? null : "Required.";
  if (!ADMOB_ID.test(v)) return "Expected ca-app-pub-<16 digits>~<10 digits>.";
  return ADMOB_APP_ID.test(v) ? null : "That's a unit id (/). App ids use a tilde: ca-app-pub-…~….";
}

/** Google's public AdMob test ids. */
export const ADMOB_TEST_IDS = {
  appId: "ca-app-pub-3940256099942544~1458002511",
  units: {
    banner: "ca-app-pub-3940256099942544/2934735716",
    interstitial: "ca-app-pub-3940256099942544/4411468910",
    rewarded: "ca-app-pub-3940256099942544/1712485313",
    rewardedInterstitial: "ca-app-pub-3940256099942544/6978759866",
    appOpen: "ca-app-pub-3940256099942544/5575463023",
  },
};

/** Google's Ad Manager sample units. */
export const ADMANAGER_TEST_IDS = {
  appId: "",
  units: {
    banner: "/21775744923/example/adaptive-banner",
    interstitial: "/21775744923/example/interstitial",
    rewarded: "/21775744923/example/rewarded",
    rewardedInterstitial: "/21775744923/example/rewarded-interstitial",
    appOpen: "/21775744923/example/app-open",
  },
};

const units = (unit: z.ZodString) => z.strictObject({ banner: unit, interstitial: unit, rewarded: unit, rewardedInterstitial: unit, appOpen: unit });
export const AdmobIds = z.strictObject({
  appId: z.string().regex(ADMOB_APP_ID, "expected an AdMob app id, ca-app-pub-<16 digits>~<10 digits>"),
  units: units(z.string().regex(ADMOB_UNIT_ID, "expected an AdMob unit id, ca-app-pub-<16 digits>/<10 digits>")),
});
export const AdManagerIds = z.strictObject({
  appId: z.union([z.literal(""), z.string().regex(ADMOB_APP_ID, 'expected "" or an app id')]),
  units: units(z.string().max(256).regex(ADMANAGER_UNIT_PATH, "expected an Ad Manager unit path, /<network code>/<unit>")),
});
export type AdIds = z.infer<typeof AdmobIds>;

// ---------------------------------------------------------------- settings

export const AD_PROVIDERS = ["admanager", "admob", "unity"] as const;
export type AdProvider = (typeof AD_PROVIDERS)[number];
export const PROVIDER_LABEL: Record<AdProvider, string> = { admanager: "Ad Manager (AdX)", admob: "AdMob", unity: "Unity" };

export const AdFormats = z.strictObject({ banner: z.boolean(), interstitial: z.boolean(), rewarded: z.boolean(), appOpen: z.boolean() });
export type AdFormats = z.infer<typeof AdFormats>;
export const FORMAT_LABEL: Record<keyof AdFormats, string> = {
  banner: "Banners",
  interstitial: "Interstitials",
  rewarded: "Rewarded",
  appOpen: "App open",
};

const cap = z.number().int().min(0).max(100);
export const AdPlacements = z.strictObject({
  rewardedExtraLevels: z.strictObject({ enabled: z.boolean(), reward: z.number().int().min(1).max(50), dailyCap: cap }),
  rewardedHint: z.strictObject({ enabled: z.boolean(), dailyCap: cap }),
  rewardedStreakFreeze: z.strictObject({ enabled: z.boolean(), weeklyCap: z.number().int().min(0).max(7) }),
  rewardedArchivePuzzle: z.strictObject({ enabled: z.boolean(), dailyCap: cap }),
  rewardedLevelRetry: z.strictObject({ enabled: z.boolean(), dailyCap: cap }),
  interstitialBetweenLevels: z.strictObject({
    enabled: z.boolean(),
    everyNLevels: z.number().int().min(1).max(100),
    minIntervalSec: z.number().int().min(0).max(86_400),
    dailyCap: cap,
  }),
  bannerLevelsMap: z.strictObject({ enabled: z.boolean() }),
  bannerStats: z.strictObject({ enabled: z.boolean() }),
  appOpen: z.strictObject({ enabled: z.boolean(), minHoursBetween: z.number().int().min(0).max(168) }),
});
export type AdPlacements = z.infer<typeof AdPlacements>;
export type PlacementKey = keyof AdPlacements;

export const COUNTRY = /^[A-Z]{2}$/;
export const AdCountries = z.strictObject({
  mode: z.enum(["all", "allow", "deny"]),
  list: z
    .array(z.string().regex(COUNTRY, 'expected ISO 3166-1 alpha-2 codes in capitals, e.g. "DE"'))
    .max(300)
    .refine((xs) => new Set(xs).size === xs.length, "codes must be distinct"),
});
export type AdCountries = z.infer<typeof AdCountries>;

// ---------------------------------------------------------------- the web (contracts/web.md §4.6)

/** Web ads: Ad Manager **web** ad unit paths and Google's consent message, or non-personalized only (`none`). */
export const WEB_AD_UNITS = ["rewarded", "banner", "interstitial"] as const;
export type WebAdUnit = (typeof WEB_AD_UNITS)[number];
export const WEB_UNIT_LABEL: Record<WebAdUnit, string> = { rewarded: "Rewarded", banner: "Banner", interstitial: "Interstitial" };
export const WEB_CMPS = ["google", "none"] as const;
export type WebCmp = (typeof WEB_CMPS)[number];
export const NETWORK_CODE = /^\d{1,20}$/;

export type AdsWeb = {
  enabled: boolean;
  networkCode: string;
  units: Record<WebAdUnit, string>;
  cmp: WebCmp;
};
export const DEFAULT_ADS_WEB: AdsWeb = { enabled: false, networkCode: "", units: { rewarded: "", banner: "", interstitial: "" }, cmp: "google" };

/** Why a web unit path is refused ("" is allowed: that format stays off), or null. */
export function webUnitProblem(networkCode: string, value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  if (ADMOB_ID.test(v)) return "That's an AdMob id. Web units are Ad Manager paths: /<network code>/<unit>.";
  if (!ADMANAGER_UNIT_PATH.test(v)) return "Expected an ad unit path: /<network code>/<unit>.";
  const code = networkCode.trim();
  if (!code) return "Set the network code first.";
  return v.startsWith(`/${code}/`) ? null : `The path must start with /${code}/, the network code.`;
}

/** Why a network code is refused, or null. Required while web ads are on or any unit is set. */
export function networkCodeProblem(web: AdsWeb): string | null {
  const code = web.networkCode.trim();
  if (!code) return web.enabled || WEB_AD_UNITS.some((u) => web.units[u].trim()) ? "Required while web ads are on or a unit is set." : null;
  return NETWORK_CODE.test(code) ? null : "Digits only, as in Ad Manager → Admin → Global settings.";
}

/** `ads.web` as PUT /config/ads.web takes it (the mock validates with this). */
const webUnit = z.union([z.literal(""), z.string().max(256).regex(ADMANAGER_UNIT_PATH, "expected an Ad Manager unit path, /<network code>/<unit>")]);
export const AdsWebSchema = z
  .strictObject({
    enabled: z.boolean(),
    networkCode: z.union([z.literal(""), z.string().regex(NETWORK_CODE, "expected the Ad Manager network code (digits)")]),
    units: z.strictObject({ rewarded: webUnit, banner: webUnit, interstitial: webUnit }),
    cmp: z.enum(WEB_CMPS),
  })
  .superRefine((v, ctx) => {
    for (const u of WEB_AD_UNITS) {
      if (v.units[u] && !v.units[u].startsWith(`/${v.networkCode}/`))
        ctx.addIssue({ code: "custom", path: ["units", u], message: `must start with /${v.networkCode || "<network code>"}/` });
    }
  });

/** Reading `ads.web`: lenient, field by field, so an older API (no key) or a partial value still shows. */
const AdsWebRead = z.object({
  enabled: z.boolean().catch(false),
  networkCode: z.string().catch(""),
  units: z
    .object({ rewarded: z.string().catch(""), banner: z.string().catch(""), interstitial: z.string().catch("") })
    .catch(DEFAULT_ADS_WEB.units),
  cmp: z.enum(WEB_CMPS).catch("google"),
});
export const readAdsWeb = (value: unknown): AdsWeb => {
  const parsed = AdsWebRead.safeParse(value);
  return parsed.success ? parsed.data : DEFAULT_ADS_WEB;
};

export const DEFAULT_PLACEMENTS: AdPlacements = {
  rewardedExtraLevels: { enabled: true, reward: 3, dailyCap: 3 },
  rewardedHint: { enabled: true, dailyCap: 5 },
  rewardedStreakFreeze: { enabled: true, weeklyCap: 1 },
  rewardedArchivePuzzle: { enabled: true, dailyCap: 1 },
  rewardedLevelRetry: { enabled: true, dailyCap: 5 },
  interstitialBetweenLevels: { enabled: true, everyNLevels: 4, minIntervalSec: 180, dailyCap: 6 },
  bannerLevelsMap: { enabled: false },
  bannerStats: { enabled: false },
  appOpen: { enabled: false, minHoursBetween: 4 },
};

/** The ads.* keys with their schemas and defaults (as backend/api). */
export const ADS_CONFIG = {
  "ads.enabled": { schema: z.boolean(), default: false },
  "ads.provider": { schema: z.enum(AD_PROVIDERS), default: "admanager" as AdProvider },
  "ads.testMode": { schema: z.boolean(), default: true },
  "ads.admob": { schema: AdmobIds, default: ADMOB_TEST_IDS },
  "ads.admanager": { schema: AdManagerIds, default: ADMANAGER_TEST_IDS },
  "ads.formats": { schema: AdFormats, default: { banner: false, interstitial: true, rewarded: true, appOpen: false } as AdFormats },
  "ads.placements": { schema: AdPlacements, default: DEFAULT_PLACEMENTS },
  "ads.newUserGraceDays": { schema: z.number().int().min(0).max(60), default: 2 },
  "ads.countries": { schema: AdCountries, default: { mode: "all", list: [] } as AdCountries },
  "ads.rewards.requireSsv": { schema: z.boolean(), default: false },
  "ads.web": { schema: AdsWebSchema, default: DEFAULT_ADS_WEB },
};
export type AdsKey = keyof typeof ADS_CONFIG;

/** Keys editors may set (contracts/ads.md §5: editors change placements; admins the IDs, switch and provider). */
export const EDITOR_ADS_KEYS: readonly string[] = ["ads.formats", "ads.placements"];

export interface AdsSettings {
  enabled: boolean;
  provider: AdProvider;
  testMode: boolean;
  admob: AdIds;
  admanager: AdIds;
  formats: AdFormats;
  placements: AdPlacements;
  graceDays: number;
  countries: AdCountries;
  requireSsv: boolean;
  /** The web (contracts/web.md §4.6); the default when the API doesn't send `ads.web`. */
  web: AdsWeb;
}

/** The ads settings from GET /config values; a missing or unreadable key reads as its default. */
export function adsSettings(values: Record<string, unknown>): AdsSettings {
  const get = <K extends AdsKey>(key: K): z.infer<(typeof ADS_CONFIG)[K]["schema"]> => {
    const parsed = ADS_CONFIG[key].schema.safeParse(values[key]);
    return (parsed.success ? parsed.data : ADS_CONFIG[key].default) as z.infer<(typeof ADS_CONFIG)[K]["schema"]>;
  };
  return {
    enabled: get("ads.enabled"),
    provider: get("ads.provider"),
    testMode: get("ads.testMode"),
    admob: get("ads.admob"),
    admanager: get("ads.admanager"),
    formats: get("ads.formats"),
    placements: get("ads.placements"),
    graceDays: get("ads.newUserGraceDays"),
    countries: get("ads.countries"),
    requireSsv: get("ads.rewards.requireSsv"),
    web: readAdsWeb(values["ads.web"]),
  };
}

// ---------------------------------------------------------------- placements editor

export type PlacementField = "reward" | "dailyCap" | "weeklyCap" | "everyNLevels" | "minIntervalSec" | "minHoursBetween";

export const PLACEMENTS: { key: PlacementKey; label: string; format: keyof AdFormats; note: string; fields: PlacementField[] }[] = [
  {
    key: "rewardedExtraLevels",
    label: "Extra levels",
    format: "rewarded",
    note: "“Today’s free levels are done” sheet: +N free levels today.",
    fields: ["reward", "dailyCap"],
  },
  { key: "rewardedHint", label: "Level hint", format: "rewarded", note: "The hint button in a level.", fields: ["dailyCap"] },
  {
    key: "rewardedStreakFreeze",
    label: "Streak freeze",
    format: "rewarded",
    note: "Streak-at-risk card: +1 freeze, within the freeze cap.",
    fields: ["weeklyCap"],
  },
  { key: "rewardedArchivePuzzle", label: "Archive puzzle", format: "rewarded", note: "Opens one archive day for 24 hours.", fields: ["dailyCap"] },
  { key: "rewardedLevelRetry", label: "Level retry", format: "rewarded", note: "A failed level: try again.", fields: ["dailyCap"] },
  {
    key: "interstitialBetweenLevels",
    label: "Between levels",
    format: "interstitial",
    note: "Only between levels, never in the daily or competitive modes.",
    fields: ["everyNLevels", "minIntervalSec", "dailyCap"],
  },
  { key: "bannerLevelsMap", label: "Levels map banner", format: "banner", note: "Adaptive banner above the tab bar.", fields: [] },
  { key: "bannerStats", label: "Stats banner", format: "banner", note: "Adaptive banner above the tab bar.", fields: [] },
  {
    key: "appOpen",
    label: "App open",
    format: "appOpen",
    note: "Returning from background only, never a cold start into the daily.",
    fields: ["minHoursBetween"],
  },
];

export const FIELD_LABEL: Record<PlacementField, { label: string; unit: string; min: number; max: number }> = {
  reward: { label: "Reward", unit: "levels", min: 1, max: 50 },
  dailyCap: { label: "Daily cap", unit: "/ UTC day", min: 0, max: 100 },
  weeklyCap: { label: "Weekly cap", unit: "/ ISO week", min: 0, max: 7 },
  everyNLevels: { label: "Every", unit: "levels", min: 1, max: 100 },
  minIntervalSec: { label: "Min interval", unit: "s", min: 0, max: 86_400 },
  minHoursBetween: { label: "Min gap", unit: "h", min: 0, max: 168 },
};

export const REWARDED_PLACEMENTS = [
  "rewardedExtraLevels",
  "rewardedHint",
  "rewardedStreakFreeze",
  "rewardedArchivePuzzle",
  "rewardedLevelRetry",
] as const;
export type RewardedPlacement = (typeof REWARDED_PLACEMENTS)[number];
export const REWARDED_LABEL: Record<RewardedPlacement, string> = {
  rewardedExtraLevels: "Extra levels",
  rewardedHint: "Hint",
  rewardedStreakFreeze: "Streak freeze",
  rewardedArchivePuzzle: "Archive puzzle",
  rewardedLevelRetry: "Level retry",
};

/** AdMob's SSV callback URL, to paste into each rewarded unit's settings in AdMob. */
export const SSV_CALLBACK_URL = "https://api.iqdaily.app/v1/ads/ssv";
